import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  FiBell,
  FiCheckCircle,
  FiXCircle,
  FiFileText,
} from "react-icons/fi";
import { useNotificacoes } from "@contexts/NotificacoesContext";
import { useUnsavedChanges } from "@contexts/UnsavedChangesContext";
import { Notificacao } from "@services/notificacaoService";
import { formatarTempoRelativo } from "@utils/tempoRelativo";

/** Ícone + cor por tipo de evento. */
function iconePorTipo(tipo: string): React.ReactNode {
  switch (tipo) {
    case "orcamento.respondido":
      return <FiCheckCircle className="h-5 w-5 text-emerald-500" />;
    case "orcamento.recusado":
      return <FiXCircle className="h-5 w-5 text-red-500" />;
    case "oc.criada":
      return <FiFileText className="h-5 w-5 text-[#4760e0]" />;
    default:
      return <FiBell className="h-5 w-5 text-gray-400" />;
  }
}

/**
 * Sino de notificações (item 6.1). Identidade dos orçamentos: cabeçalho escuro
 * #161519 + acento verde-limão #d2fa52. Feed compartilhado, marcação de leitura
 * individual, navegação ao contexto (respeitando alterações não salvas).
 *
 * O painel é renderizado via portal no body com position:fixed e z-index alto,
 * para SEMPRE ficar acima de cabeçalhos fixos/sticky da planilha (que criam
 * seus próprios contextos de empilhamento).
 */
const NotificationBell: React.FC = () => {
  const {
    naoLidas,
    notificacoes,
    carregando,
    carregarLista,
    marcarLida,
    marcarTodasLidas,
  } = useNotificacoes();
  const { guardNavigation } = useUnsavedChanges();
  const navigate = useNavigate();
  const [aberto, setAberto] = useState(false);
  const [coords, setCoords] = useState<{ top: number; right: number }>({
    top: 0,
    right: 0,
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const posicionar = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setCoords({
      top: r.bottom + 8,
      right: Math.max(8, window.innerWidth - r.right),
    });
  }, []);

  const fechar = () => setAberto(false);
  const toggle = () => {
    setAberto((prev) => {
      const novo = !prev;
      if (novo) {
        posicionar();
        carregarLista();
      }
      return novo;
    });
  };

  // Fechar ao clicar fora (sino + painel) ou Esc; reposicionar no resize.
  useEffect(() => {
    if (!aberto) return;
    const onDown = (e: MouseEvent) => {
      const alvo = e.target as Node;
      if (
        containerRef.current?.contains(alvo) ||
        panelRef.current?.contains(alvo)
      ) {
        return;
      }
      fechar();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") fechar();
    };
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    window.addEventListener("resize", posicionar);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", posicionar);
    };
  }, [aberto, posicionar]);

  const abrirNotificacao = (n: Notificacao) => {
    marcarLida(n.id);
    fechar();
    if (n.link) {
      guardNavigation(() => navigate(n.link!));
    }
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={toggle}
        className="btn btn-ghost btn-circle relative"
        aria-label={`Notificações${naoLidas > 0 ? `, ${naoLidas} não lidas` : ""}`}
        aria-haspopup="true"
        aria-expanded={aberto}
      >
        <FiBell className="h-5 w-5" />
        {naoLidas > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#d2fa52] text-[#161519] text-[10px] font-bold flex items-center justify-center leading-none">
            {naoLidas > 9 ? "9+" : naoLidas}
          </span>
        )}
      </button>

      {aberto &&
        createPortal(
          <div
            ref={panelRef}
            role="dialog"
            aria-label="Notificações"
            className="fixed z-10000 w-96 max-w-[calc(100vw-1rem)] bg-white rounded-2xl shadow-2xl border border-base-300 overflow-hidden"
            style={{ top: coords.top, right: coords.right }}
          >
            {/* Cabeçalho escuro (identidade dos orçamentos) */}
            <div className="bg-[#161519] px-4 py-3 flex items-center justify-between gap-3">
              <span className="text-white font-bold">Notificações</span>
              {naoLidas > 0 && (
                <button
                  type="button"
                  className="text-[#d2fa52] text-xs font-semibold hover:underline"
                  onClick={marcarTodasLidas}
                >
                  Marcar todas como lidas
                </button>
              )}
            </div>

            {/* Corpo */}
            <div className="max-h-96 overflow-y-auto">
              {carregando ? (
                <div className="p-4 space-y-3">
                  {[0, 1, 2].map((i) => (
                    <div key={i} className="flex gap-3 animate-pulse">
                      <div className="w-8 h-8 rounded-lg bg-base-200 shrink-0" />
                      <div className="flex-1 space-y-2 py-1">
                        <div className="h-3 bg-base-200 rounded w-2/3" />
                        <div className="h-3 bg-base-200 rounded w-full" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : notificacoes.length === 0 ? (
                <div className="flex flex-col items-center justify-center text-center py-10 px-6 text-gray-400">
                  <FiBell className="h-8 w-8 mb-2 opacity-40" />
                  <p className="text-sm">Sem novidades por aqui.</p>
                </div>
              ) : (
                <ul>
                  {notificacoes.map((n) => (
                    <li key={n.id}>
                      <button
                        type="button"
                        onClick={() => abrirNotificacao(n)}
                        className={`w-full text-left flex gap-3 px-4 py-3 border-b border-base-200 transition-colors hover:bg-base-100 ${
                          !n.lidaEm ? "bg-[#d2fa52]/10" : ""
                        }`}
                      >
                        <span className="mt-0.5 shrink-0">
                          {iconePorTipo(n.tipo)}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-sm text-[#161519] truncate">
                              {n.titulo}
                            </span>
                            {!n.lidaEm && (
                              <span className="w-2 h-2 rounded-full bg-[#d2fa52] shrink-0" />
                            )}
                          </div>
                          {n.mensagem && (
                            <p className="text-xs text-gray-600 line-clamp-2 mt-0.5">
                              {n.mensagem}
                            </p>
                          )}
                          <span className="text-[11px] text-gray-400">
                            {formatarTempoRelativo(n.criadaEm)}
                          </span>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default NotificationBell;
